import { useTranslation } from 'react-i18next';
import { Paper, Typography, Button, Box, Alert, Tooltip } from '@gapartners/ui';
import { api } from '@/store/api';
import { useAmazonConnectContacts } from '@/hooks/useAmazonConnectContacts';
import {
  getInitialOrCaptureContactId,
  isChatContact,
} from '@/components/amazon-connect/contactUtils';
import type { QuickScript } from '@/types/quickScripts';
import { useAppSelector } from '@/store/hooks';

interface QuickScriptsCardProps {
  onScriptClick: (script: QuickScript) => void;
}

export function QuickScriptsCard({ onScriptClick }: QuickScriptsCardProps) {
  const { t } = useTranslation();
  const { useGetQuickScriptsQuery } = api;
  const { data: scripts = [], isLoading, error } = useGetQuickScriptsQuery();

  const contactId = useAppSelector(state => state.call.contactId);
  const { activeContacts } = useAmazonConnectContacts();
  const activeContact = activeContacts.find(c => {
    if (!contactId) return false;
    try {
      return (
        c.getContactId() === contactId ||
        getInitialOrCaptureContactId(c) === contactId
      );
    } catch {
      return false;
    }
  });
  const isVoiceCall = activeContact != null && !isChatContact(activeContact);

  if (isLoading) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '150px',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('common.loading')}
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Alert severity="error" sx={{ fontSize: '0.875rem' }}>
          {t('components.contactCenter.errorLoadingQuickScripts')}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
        {t('components.contactCenter.quickScripts')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxHeight: '400px',
          overflowY: 'auto',
        }}
      >
        {scripts.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {t('components.contactCenter.noQuickScripts')}
          </Typography>
        ) : (
          scripts.map(script => (
            <Tooltip
              key={script.id}
              title={script.content}
              placement="left"
              arrow
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  bgcolor: 'action.hover',
                  p: 1,
                  borderRadius: 1,
                  transition: 'all 0.2s',
                  '&:hover': isVoiceCall ? {} : { bgcolor: 'action.selected' },
                }}
              >
                {isVoiceCall ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      width: '100%',
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        pl: 0.5,
                      }}
                    >
                      {script.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontStyle: 'italic', pl: 0.5 }}
                    >
                      {t('components.contactCenter.readAloudToClient')}
                    </Typography>
                  </Box>
                ) : (
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => onScriptClick(script)}
                    sx={{
                      justifyContent: 'flex-start',
                      color: 'text.primary',
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      textAlign: 'left',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      p: 0,
                      ml: 0.5,
                    }}
                  >
                    {script.title}
                  </Button>
                )}
              </Box>
            </Tooltip>
          ))
        )}
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 2 }}
      >
        {t('components.contactCenter.quickScriptsHelpText')}
      </Typography>
    </Paper>
  );
}
